import { Button } from 'components/Button';
import { Modal } from 'components/modal/Modal';
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Table } from 'components/table/Table';
import { useUser } from 'components/user/UserProvider';
import { ClientListQuery } from 'graphql/Client/queries/ClientListQuery';
import { useQuery } from 'libs/react';
import { FormIconCheckBox } from './FormIconCheckBox';
import { FormInput } from './FormInput';
import { FormSelect } from './FormSelect';
import './FormInputPickerModal.scss';
/**
 * Partner filter value showing every row.
 * @const PARTNER_ALL
 * @since 1.0.0
 * @hidden
 */
const PARTNER_ALL = 'all';
/**
 * Partner filter value showing the rows available to everyone, like the
 * partner filter of the catalog.
 * @const PARTNER_EVERYONE
 * @since 1.0.0
 * @hidden
 */
const PARTNER_EVERYONE = 'everyone';
/**
 * Converts HTML infos into a single line of plain text.
 * @function toText
 * @since 1.0.0
 * @hidden
 */
function toText(html) {
    var _a;
    if (!html) {
        return '';
    }
    if (!/[<&]/.test(html)) {
        return html.trim();
    }
    const element = document.createElement('div');
    element.innerHTML = html;
    return ((_a = element.textContent) !== null && _a !== void 0 ? _a : '').replace(/\s+/g, ' ').trim();
}
/**
 * @component FormInputPickerModal
 * @since 1.0.0
 * @hidden
 */
export function FormInputPickerModal(props) {
    const { creatable, items, format, onClose, onChangeMany, partnerFilter, ready, singlePick, title, values } = Object.assign({ singlePick: false }, props);
    const user = useUser();
    const searchRef = useRef(null);
    const canFilterByPartner = !!partnerFilter && user.can('saleItem.partnerExclusivity');
    const [partnersData] = useQuery(ClientListQuery, {
        variables: {
            input: {
                isEndClient: false,
                limit: 500
            }
        },
        skip: !canFilterByPartner
    });
    const [search, setSearch] = useState('');
    const [selectionOnly, setSelectionOnly] = useState(false);
    const [partner, setPartner] = useState(PARTNER_ALL);
    const initial = useMemo(() => {
        const map = new Map();
        values === null || values === void 0 ? void 0 : values.forEach((value) => map.set(format(value).id, value));
        return map;
    }, []);
    const [pending, setPending] = useState(() => new Map(initial));
    const rows = useMemo(() => (items !== null && items !== void 0 ? items : []).map((item) => {
        var _a;
        const value = format(item);
        const infos = toText(value.infos);
        return Object.assign(Object.assign({}, value), { infos,
            item, text: [
                value.title,
                value.category,
                infos,
                ...((_a = value.partners) !== null && _a !== void 0 ? _a : []).map((partner) => partner.name)
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase() });
    }), [items]);
    const partnerOptions = useMemo(() => {
        var _a, _b;
        const partners = new Map();
        if (!canFilterByPartner) {
            return [];
        }
        (_b = (_a = partnersData === null || partnersData === void 0 ? void 0 : partnersData.clients) === null || _a === void 0 ? void 0 : _a.items) === null || _b === void 0 ? void 0 : _b.forEach((client) => { var _a; return partners.set(client.id, (_a = client.name) !== null && _a !== void 0 ? _a : ''); });
        rows.forEach((row) => {
            var _a;
            return (_a = row.partners) === null || _a === void 0 ? void 0 : _a.forEach((partner) => { var _a; return partners.set(partner.id, (_a = partner.name) !== null && _a !== void 0 ? _a : ''); });
        });
        return Array.from(partners.entries())
            .map(([value, label]) => ({ value, label }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [rows, partnersData, canFilterByPartner]);
    const filteredRows = useMemo(() => {
        const query = search.trim().toLowerCase();
        return rows.filter((row) => {
            var _a, _b;
            if (selectionOnly && !pending.has(row.id)) {
                return false;
            }
            if (partner === PARTNER_EVERYONE && ((_a = row.partners) === null || _a === void 0 ? void 0 : _a.length)) {
                return false;
            }
            if (partner !== PARTNER_ALL &&
                partner !== PARTNER_EVERYONE &&
                !((_b = row.partners) === null || _b === void 0 ? void 0 : _b.some((rowPartner) => rowPartner.id === partner))) {
                return false;
            }
            return query === '' || row.text.includes(query);
        });
    }, [rows, search, selectionOnly, partner, pending]);
    const added = Array.from(pending.entries())
        .filter(([id]) => !initial.has(id))
        .map(([, item]) => item);
    const removed = Array.from(initial.entries())
        .filter(([id]) => !pending.has(id))
        .map(([, item]) => item);
    const hasChanges = added.length > 0 || removed.length > 0;
    const hasCategory = rows.some((row) => row.category);
    const hasInfos = rows.some((row) => row.infos);
    const allSelected = filteredRows.length > 0 && filteredRows.every((row) => pending.has(row.id));
    /**
     * Focuses the search field when the modal opens.
     * @since 1.0.0
     */
    useEffect(() => {
        var _a, _b;
        (_b = (_a = searchRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('input')) === null || _b === void 0 ? void 0 : _b.focus();
    }, []);
    /**
     * @function onToggle
     * @since 1.0.0
     * @hidden
     */
    function onToggle(row) {
        if (singlePick) {
            props.onSelect(row.item);
            onClose();
            return;
        }
        setPending((current) => {
            const next = new Map(current);
            if (next.has(row.id)) {
                next.delete(row.id);
            }
            else {
                next.set(row.id, row.item);
            }
            return next;
        });
    }
    /**
     * @function onToggleAll
     * @since 1.0.0
     * @hidden
     */
    function onToggleAll() {
        setPending((current) => {
            const next = new Map(current);
            filteredRows.forEach((row) => {
                if (allSelected) {
                    next.delete(row.id);
                }
                else {
                    next.set(row.id, row.item);
                }
            });
            return next;
        });
    }
    /**
     * @function onConfirm
     * @since 1.0.0
     * @hidden
     */
    function onConfirm() {
        if (onChangeMany) {
            onChangeMany(added, removed);
        }
        else {
            removed.forEach((item) => props.onRemove(item));
            added.forEach((item) => props.onSelect(item));
        }
        onClose();
    }
    /**
     * @function onSearchKeyDown
     * @since 1.0.0
     * @hidden
     */
    function onSearchKeyDown(e) {
        if (e.key === 'Enter' && singlePick && filteredRows.length === 1) {
            e.preventDefault();
            onToggle(filteredRows[0]);
        }
    }
    const schema = {};
    schema.select = {
        header: '',
        width: 'min',
        render: (row) => (React.createElement(FormIconCheckBox, { checkedColor: "primary", checkedIcon: "fal check", uncheckedIcon: "fal plus", checked: pending.has(row.id), onChange: () => onToggle(row) }))
    };
    schema.title = {
        header: t('Title'),
        render: (row) => (React.createElement("span", { className: "form-input-picker-modal-title" }, row.title))
    };
    if (hasCategory) {
        schema.category = {
            header: t('Category'),
            render: (row) => (React.createElement("span", { className: "form-input-picker-modal-category" }, row.category))
        };
    }
    if (hasInfos) {
        schema.infos = {
            header: t('Infos'),
            render: (row) => (React.createElement("span", { className: "form-input-picker-modal-infos", title: row.infos }, row.infos))
        };
    }
    const footerActions = (React.createElement(Fragment, null,
        React.createElement("div", { className: "form-input-picker-modal-summary" },
            !singlePick && (React.createElement("span", { className: "form-input-picker-modal-summary-selected" }, t('{{count}} selected').replace('{{count}}', String(pending.size)))),
            React.createElement("span", null, t('{{count}} results').replace('{{count}}', String(filteredRows.length)))),
        React.createElement("div", { className: "form-input-picker-modal-actions" },
            React.createElement(Button, { style: "outline", label: t('Cancel'), onPress: onClose }),
            !singlePick && (React.createElement(Button, { style: "primary", label: added.length > 0
                    ? t('Add {{count}} elements').replace('{{count}}', String(added.length))
                    : t('Apply'), disabled: !hasChanges, onPress: onConfirm })))));
    return (React.createElement("div", { className: "form-input-picker-modal" },
        React.createElement(Modal, { picto: "list", title: title !== null && title !== void 0 ? title : t('Select'), showFooterAction: true, footerActions: footerActions, onClose: onClose },
            React.createElement("div", { className: "form-input-picker-modal-content" },
                React.createElement("div", { className: "form-input-picker-modal-toolbar" },
                    React.createElement("div", { className: "form-input-picker-modal-filters" },
                        React.createElement("div", { className: "form-input-picker-modal-search", ref: searchRef },
                            React.createElement(FormInput, { picto: "fas search", placeholder: t('Search'), value: search, clearable: true, onChange: setSearch, onKeyDown: onSearchKeyDown, size: "small" })),
                        partnerOptions.length > 0 && (React.createElement(FormSelect, { className: "form-input-picker-modal-partner", name: "partner", size: "small", value: partner, skipSort: true, options: [
                                { value: PARTNER_ALL, label: t('All partners') },
                                { value: PARTNER_EVERYONE, label: t('Everyone') },
                                ...partnerOptions
                            ], onChange: (value) => setPartner(value || PARTNER_ALL) }))),
                    React.createElement("div", { className: "form-input-picker-modal-toolbar-actions" },
                        !singlePick && (React.createElement(Fragment, null,
                            React.createElement(Button, { style: selectionOnly ? 'primary-100' : 'outline', size: "small", picto: "check", label: t('Selection ({{count}})').replace('{{count}}', String(pending.size)), disabled: pending.size === 0 && !selectionOnly, onPress: () => setSelectionOnly(!selectionOnly) }),
                            React.createElement(Button, { style: "outline", size: "small", label: allSelected ? t('Deselect All') : t('Select All'), disabled: filteredRows.length === 0, onPress: onToggleAll }))),
                        creatable && (React.createElement(Button, { style: "outline", size: "small", picto: "add", label: t('Create'), onPress: () => {
                                var _a;
                                onClose();
                                (_a = props.onCreate) === null || _a === void 0 ? void 0 : _a.call(props);
                            } })))),
                React.createElement(Table, { ready: ready, className: "form-input-picker-modal-table", schema: schema, values: filteredRows, sortable: [], primaryColumn: "title", onRowClick: (row) => onToggle(row), emptyMessageTitle: t('No results found') })))));
}
